/**
 * Small, pure text helpers for the Salesforce sync screens. Times use the
 * browser's own time zone and 24 hour clock, as the approved mockups do
 * ("10:42"), and are built from the date's own parts, so a spec gives the same
 * answer in every time zone.
 */

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

const toDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatNumber = (n: number): string =>
  Number.isFinite(n) ? n.toLocaleString("en-US") : "0";

/** "10:42", or "" when there is no time. */
export const formatClock = (iso: string | null | undefined): string => {
  const date = toDate(iso);
  return date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : "";
};

/** "8 Oct 10:42": the time with its date, whatever the day. "" when there is no time. */
export const formatDated = (iso: string | null | undefined): string => {
  const date = toDate(iso);
  return date
    ? `${date.getDate()} ${MONTHS[date.getMonth()]} ${formatClock(iso)}`
    : "";
};

/** "10:42" today, "8 Oct 10:42" on another day, "" when there is no time. */
export const formatWhen = (
  iso: string | null | undefined,
  nowMs: number
): string => {
  const date = toDate(iso);
  if (!date) return "";
  const now = new Date(nowMs);
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  return sameDay ? formatClock(iso) : formatDated(iso);
};

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** "4 minutes ago", "In about 1 minute". "" when there is no time. */
export const formatRelative = (
  iso: string | null | undefined,
  nowMs: number
): string => {
  const date = toDate(iso);
  if (!date) return "";
  const diff = date.getTime() - nowMs;
  const past = diff < 0;
  const abs = Math.abs(diff);
  if (abs < 45 * 1000) return past ? "Just now" : "In less than a minute";
  let span: string;
  if (abs < 90 * 60 * 1000) {
    span = plural(Math.max(1, Math.round(abs / 60000)), "minute", "minutes");
  } else if (abs < 36 * 60 * 60 * 1000) {
    span = plural(Math.round(abs / 3600000), "hour", "hours");
  } else {
    span = plural(Math.round(abs / 86400000), "day", "days");
  }
  return past ? `${span} ago` : `In about ${span}`;
};

/** "Lead", "Lead and Contact", "Lead, Contact and Account". */
export const listOf = (names: string[]): string =>
  names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

export const countOf = (n: number, one: string, many: string): string =>
  plural(n, one, many);
